import { formatPrice } from '@ynap/intl-formatters'
import { getAnalyticsConfig } from '@ynap/config-state'
import { getCountries } from '@ynap/country-state'
import { getCardTypes, getLocale, getConfig, getShippingOptions } from '@ynap/cart-state'
import { getCurrentShippingAddress, getSavedAddresses, mapAddressFields } from './getShippingAddressStep'
import cardValidator from 'card-validator'
import { GPStoValidator } from '../actions/common/mapCardType'
import getCart, { getUserStatus, getPaymentSystem } from './getCart'
import isPaymentRequired from './isPaymentRequired'
import { STORE_CREDIT, CREDIT_CARD, PAYPAL } from '../constants/PaymentMethods'
import { browserInfoKeys } from '../../../adyen'
import { createCombinedSelector } from '../utils/selectorUtils'
import { isFullyShippingRestricted } from './getShippingRestrictions'

import { StoreState } from 'app/state/types'
import { ActivePromotions, CheckerInfo } from 'app/components/PromoCodesSection/types'
import { AddAddress } from '../reducers/addAddress'
import { FetchCartError } from '../reducers/fetchCartError'
import { CheckoutError } from '../reducers/checkoutError'
import { RemovePromotionError } from '../reducers/removePromotionError'
import { Address, Messages, PaymentInstruction, ShoppingCart, type PaymentOption } from 'app/types/common-types'

export default createCombinedSelector({
    name: 'getPaymentStep',
    state: [
        'locale',
        'config',
        'countries',
        'cart',
        'deviceType',
        'isFetchingCart',
        'fetchCartError',
        'checkoutError',
        'removePromotionError',
        'addAddress',
        'activePromotions',
        'promotionsCheckerInfo',
        'isFetchingProvinces',
        'isFetchingAllProvinces',
    ],
    props: ['messages'],
    fn: getPaymentStepInternal,
})

interface State extends StoreState {
    isFetchingCart : boolean,
    fetchCartError: FetchCartError,
    checkoutError: CheckoutError,
    removePromotionError: RemovePromotionError,
    addAddress: AddAddress ,
    activePromotions: ActivePromotions,
    promotionsCheckerInfo: Record<string, CheckerInfo>,
    isFetchingProvinces: boolean,
    isFetchingAllProvinces: boolean
    isShippingAddressValid: (state: boolean, action: Record<string, string>) => boolean,
}

type Props = {
    messages: Messages
}

export function getPaymentStepInternal(state: State, props: Props) {
    const config = getConfig(state)
    const {
        isFetchingCart,
        fetchCartError,
        checkoutError,
        removePromotionError = {},
        addAddress,
        activePromotions,
        promotionsCheckerInfo,
        isFetchingProvinces,
        isFetchingAllProvinces,
        isShippingAddressValid,
    } = state

    const paymentOptions = getPaymentOptions(state)
    const paymentSystem = getPaymentSystem(state)
    const savedAddresses = getSavedAddresses(state)
    const currentBillingAddress = getCurrentBillingAddress(state)
    const currentShippingAddress = getCurrentShippingAddress(state)
    const cardTypeInfo = getCardTypeInfo(state)
    const userStatus = getUserStatus(state)
    const cvvRequired = isCvvRequired(state)
    const deviceType = state.deviceType || ''
    const loading = isFetchingCart
    const error = fetchCartError || !paymentOptions || !paymentOptions.length
    const completed = isCompleted(paymentOptions, currentBillingAddress)
    const paymentRequired = isPaymentRequired(state)
    const paymentMethodError = hasPaymentMethodError(state)
    const {
        lineItems,
        totals,
        genericTotal,
        isCSR,
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics,
        deliveryDuty,
        dduAcceptance,
        getApplePayRequest,
        hasShippingMethodChanged,
        orderItem,
    } = getCart(state, props)
    const { countries = {} } = getCountries(state)
    const { paypal = {} } = config?.head || {}

    const [currentShippingOptions] = getShippingOptions(state, props)
    const currentShippingOption = currentShippingOptions?.find((option) => option.selected)
    const isPremierAvailable = !!currentShippingOptions?.find((option) => option.shipModeCode?.toLowerCase().includes('premier'))
    const isPremierSelected = currentShippingOption?.shipModeCode?.toLowerCase().includes('premier')
    const shouldAskDDU = deliveryDuty === 'DDU' && dduAcceptance !== 'true'

    // Logic used to determine if Shipping Anddress and Shipping Options steps are complete
    const hasOrderItemAddress = !!orderItem[0]?.addressLine?.[0]
    const shippingRestricted = isFullyShippingRestricted(orderItem)
    const isSelectedAddressValid = !(isShippingAddressValid === false)
    // TODO: using 'undefined' instead of 'false' to align with steps logic. Using 'false' will stop steps from being considered complete.
    const shippingAddressStepComplete = currentShippingAddress ? hasOrderItemAddress && isSelectedAddressValid && !shippingRestricted : undefined
    const shippingOptionsStepComplete = currentShippingOptions ? !!currentShippingOption : undefined
    const previousStepsComplete = shippingAddressStepComplete && shippingOptionsStepComplete

    return {
        config,
        activePromotions,
        promotionsCheckerInfo,
        paymentOptions,
        paymentSystem,
        savedAddresses: currentBillingAddress?.transient && savedAddresses ? [currentBillingAddress, ...savedAddresses] : savedAddresses,
        currentBillingAddress,
        currentShippingAddress,
        currentShippingOption,
        cardTypeInfo,
        cvvRequired,
        userStatus,
        isCSR,
        lineItems,
        totals,
        checkoutError,
        loading,
        error,
        completed,
        paymentRequired,
        removePromotionError,
        analytics: getAnalyticsConfig(state),
        genericTotal,
        countries,
        paypalBAEnabled: !!paypal.billingAgreementEnabled,
        x_exchangeRate,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal,
        checkoutAnalytics: {
            ...checkoutAnalytics,
            shippingAddress: currentShippingAddress,
            shippingOption: currentShippingOption,
        },
        addAddress,
        deviceType,
        isPremierAvailable,
        isPremierSelected,
        dduInfo: {
            dduAcceptance,
            deliveryDuty,
            shouldAskDDU,
        },
        getApplePayRequest,
        hasShippingMethodChanged,
        isFetchingProvinces,
        isFetchingAllProvinces,
        previousStepsComplete,
        paymentMethodError,
    }
}

// When cart.x_cvvRequired is present and true, we need to get the user to confirm their CVV
export function isCvvRequired(state) {
    const { cart = {} } = state
    if (cart.x_cvvRequired === false) {
        return false
    } else {
        return true
    }
}

export function getCardTypeInfo(state) {
    const { cart = {} } = state
    const { paymentInstruction = [] } = cart

    const creditCardInstruction = paymentInstruction ? paymentInstruction.find((instruction) => instruction.payMethodId === 'CREDIT_CARD') : undefined

    // Extract the object that has the card type.
    const cardTypeObj =
        creditCardInstruction && creditCardInstruction.protocolData ? creditCardInstruction.protocolData.find((data) => data.name === 'cc_type') : undefined
    // Get the card type value from the object.
    const cardType = cardTypeObj ? cardTypeObj.value : undefined

    // Get the corresponding card type object as defined by card-validator.
    // This object contains extra info such as cvv size (number of digits),
    // the string name of the cvv (CVC, CVV, CID, etc.)
    // and whether or not it's Amex (which determines whether
    // the cvv is on the front or back of card).
    const cardTypeInfo = cardValidator.creditCardType.getTypeInfo(GPStoValidator(cardType))

    // don't need all of it, just return the fields needed.
    return cardTypeInfo
        ? {
              cardType: cardTypeInfo.type,
              cvvType: cardTypeInfo.code.name,
              cvvSize: cardTypeInfo.code.size,
          }
        : { cvvType: 'CVV', cvvSize: 0 }
}

export function getCreditCardPaymentInfo(state) {
    const { cart = {} } = state
    const { paymentInstruction = [] } = cart
    const creditCardInstruction = paymentInstruction ? paymentInstruction.find((instruction) => instruction.payMethodId === CREDIT_CARD) : undefined

    return creditCardInstruction
        ? {
              cardId: creditCardInstruction.creditCardDetails.card.apiToken,
              piId: creditCardInstruction.piId,
              ...getCardTypeInfo(state),
          }
        : {}
}

export function pickProtocolData(protocolData: Record<string,string>[] | undefined = [], keys) {
    return protocolData.reduce((acc, item) => {
        if (keys.includes(item.name)) {
            return {
                ...acc,
                [item.name]: item.value,
            }
        }
        return acc
    }, {})
}

export function getPaymentOptions(state: State): PaymentOption[] {
    const locale = getLocale(state)
    const { cart = {} } = state
    const { usablePaymentInformation, savedCardPayments: { walletItem } = {}, paymentInstruction } = cart as ShoppingCart
    let hasSelectedPaymentType = false

    if (usablePaymentInformation) {
        const paymentRequired = isPaymentRequired(state)

        // Do not show prices for payment options if all of them are free
        const showPrices = usablePaymentInformation.find((paymentOption) => paymentOption.xumet_charge && paymentOption.xumet_charge.amount > 0)

        const paymentOptions = usablePaymentInformation.reduce((accumulator, paymentOption) => {
            const {
                paymentMethodName,
                description,
                xumet_charge,
                xumet_currency,
                xumet_card_types,
                xumet_billingAgreement,
                xumet_billingAgreementEmail,
                xumet_availableAmount = {},
            } = paymentOption

            const storeCredit = paymentMethodName === STORE_CREDIT
            const hasStoreCredit = xumet_availableAmount.amount > 0
            const keepOption = storeCredit ? hasStoreCredit : paymentRequired
            const id = paymentMethodName === PAYPAL && xumet_billingAgreement ? `${PAYPAL}/${xumet_billingAgreement}` : paymentMethodName

            if (keepOption) {
                accumulator[id] = {
                    id,
                    description,
                    types: getCardTypes(xumet_card_types),
                    price: showPrices && xumet_charge ? formatPrice(xumet_charge.amount / xumet_charge.divisor, xumet_currency.label, locale.id) : undefined,
                    ...(xumet_billingAgreement
                        ? {
                              billingAgreementToken: xumet_billingAgreement,
                              billingAgreementEmail: xumet_billingAgreementEmail,
                          }
                        : {}),
                }
            }

            return accumulator
        }, {})

        // Include available payment options only if further payment is required
        if (paymentRequired) {
            // Include saved cards from wallet
            if (walletItem) {
                walletItem.reduce((accumulator, item) => {
                    const id = `${CREDIT_CARD}/${item.cardToken}`
                    const card_holder = item.card_holder?.name || (item.card_holder?.first_name && item.card_holder?.last_name) ? item.card_holder : {}
                    accumulator[id] = {
                        id,
                        cardId: item.cardToken,
                        type: item.card.code,
                        expireMonth: item.card.expire_month,
                        expireYear: item.card.expire_year?.slice(-2),
                        lastFourDigits: item.card.last_four_digits,
                        saved: true,
                        expired: item.expired,
                        card_holder,
                    }

                    return accumulator
                }, paymentOptions)
            }
        }

        // Include current card and mark current payment options as selected
        if (paymentInstruction) {
            paymentInstruction.reduce((accumulator, payInstruction) => {
                const { payMethodId, creditCardDetails, addressLine, piAmount, piCurrency, x_creditAmount, protocolData } = payInstruction as PaymentInstruction
                const returnUrls = pickProtocolData(payInstruction.protocolData, ['returnUrl', 'cancelUrl'])
                const browserInfo = pickProtocolData(payInstruction.protocolData, browserInfoKeys)
                const paypalBillingAgreement = pickProtocolData(payInstruction.protocolData, ['paypalBillingAgreement'])?.paypalBillingAgreement

                if (creditCardDetails) {
                    const { card } = creditCardDetails
                    const id = `${payMethodId}/${card.apiToken}`

                    accumulator[id] = {
                        ...accumulator[id], // If also a saved card make sure those props are kept
                        id,
                        cardId: card.apiToken,
                        type: card.code,
                        expireMonth: card.expire_month,
                        expireYear: card.expire_year?.slice(-2),
                        lastFourDigits: card.last_four_digits,
                        addressLine: addressLine?.[0],
                        selected: true,
                        ...returnUrls,
                        browserInfo,
                        card_holder: payInstruction.creditCardDetails.card_holder || {},
                    }
                    hasSelectedPaymentType = true
                } else if (paypalBillingAgreement) {
                    const id = `${payMethodId}/${paypalBillingAgreement}`
                    accumulator[id] = {
                        ...accumulator[id],
                        id,
                        amount: piAmount && locale ? formatPrice(piAmount.amount / piAmount.divisor, piCurrency.label, locale.id) : undefined,
                        creditAmount:
                            x_creditAmount && locale ? formatPrice(x_creditAmount.amount / x_creditAmount.divisor, piCurrency.label, locale.id) : undefined,
                        selected: true,
                        ...returnUrls,
                        browserInfo,
                    }
                } else if (payMethodId in accumulator) {
                    const notStoreCredit = payMethodId !== STORE_CREDIT
                    const paymentFullyCoveredByStoreCredit = !paymentRequired
                    const selected = !hasSelectedPaymentType && (notStoreCredit || paymentFullyCoveredByStoreCredit)
                    const lastFourDigits = protocolData?.find((data) => data.name === 'cc_lastFourDigits')?.value
                    accumulator[payMethodId] = {
                        ...accumulator[payMethodId],
                        amount: piAmount && locale ? formatPrice(piAmount.amount / piAmount.divisor, piCurrency.label, locale.id) : undefined,
                        creditAmount:
                            x_creditAmount && locale ? formatPrice(x_creditAmount.amount / x_creditAmount.divisor, piCurrency.label, locale.id) : undefined,
                        selected,
                        ...returnUrls,
                        browserInfo,
                        ...(lastFourDigits && { lastFourDigits }),
                    }
                }

                return accumulator
            }, paymentOptions)
        }

        return values(paymentOptions)
    }
}

export function getCurrentPaymentOption(state): PaymentOption | undefined {
    const paymentOptions = getPaymentOptions(state)
    if (paymentOptions) {
        return paymentOptions.find(({ selected }) => selected)
    }
}

export function getCurrentPaymentList(state) {
    const { cart = {} } = state
    const { paymentInstruction = [] } = cart
    return paymentInstruction.map((option) => option.payMethodId)
}

export function getSavedCardById(state, cardId) {
    const paymentOptions = getPaymentOptions(state)
    if (paymentOptions) {
        return paymentOptions.find((card) => card.cardId && card.cardId === cardId)
    }
}

export function getCurrentBillingAddress(state) {
    const { cart = {} } = state
    const { paymentInstruction = [] } = cart
    const billingAddress = paymentInstruction.filter(removeIfUndefinedAsString).find(({ billing_address_id }) => !!billing_address_id)

    if (billingAddress) {
        // Extract address fields from payment instruction
        return { ...mapAddressFields(billingAddress, state), addressId: billingAddress.billing_address_id }
    }
}

export function isCompleted(paymentOptions, currentBillingAddress) {
    const isValidPayment =
        paymentOptions &&
        paymentOptions.some(({ id, selected }) => {
            // Saved credit cards will have id CREDIT_CARD/{apiToken}
            const notDummyCreditCard = id !== CREDIT_CARD

            return selected && notDummyCreditCard
        })
    // Tech debt: Due implementation detail of the Step component, need to return undefined when paymentOptions undefined for correct behaviour
    return isValidPayment && !!currentBillingAddress
}

// Object.values polyfill
// TODO - polyfill should be taken care of by babel
function values(obj) {
    return Object.keys(obj).map((key) => obj[key])
}

function hasPaymentMethodError(state) {
    if (state.paymentMethodError === true) {
        return true
    }
    return false
}
/**  Tempory solution for screnario where AP express is cancelled
 * We check if some fields are:
 * firstname undefined as {string}
 * lastname undefined as {string}
 * addressLine undefined as string[]
 * if we do we remove it form the return
 * @params bilingAddress {Object}
 */
function removeIfUndefinedAsString(billingAddress: Address):Address | undefined  {
    const { firstName, lastName, addressLine } = billingAddress
    if (firstName === 'undefined' || lastName === 'undefined' || (addressLine && addressLine[0] === 'undefined')) {
        return
    }
    return billingAddress
}
